import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import { FEATURES } from 'dpl/shared/constants/features';
import { LITTER_STATES } from 'dpl/shared/constants/shared';
import { isMobileUA } from 'dpl/shared/utils';

import {
  LITTER_GALLERY_CATEGORIES,
  LitterPageGalleryItemResource,
  LitterPageHeroPhotoResource
} from '../constants/resource_types';

/** Mom first, then dad — the order the grid renders its two columns in. */
export const LITTER_MEDIA_ROLES = ['mom', 'dad'] as const;

export type LitterMediaRole = (typeof LITTER_MEDIA_ROLES)[number];

const PAST_LITTER_LABEL = 'Past puppies from parents';

const IS_LITTER_PAGE_ENABLED = isFeatureEnabled(FEATURES.REL_LITTER_PAGE);
const IS_MOBILE = isMobileUA();

// The page only serves planned and expected litters, so anything else — born,
// archived or hidden — keeps opening the litter modal.
const LITTER_PAGE_STATUSES: (string | null | undefined)[] = [
  LITTER_STATES.PLANNED,
  LITTER_STATES.EXPECTED
];

function formatParentLabel(name: string | undefined, role: LitterMediaRole) {
  return `${name || 'TBD'}, ${role}`;
}

export interface LitterMediaSlot {
  role: LitterMediaRole;
  /** e.g. "Bristol, mom" or "TBD, dad". */
  label: string;
  /**
   * Null for a TBD parent, for one with no profile photo, and for one the
   * litter doesn't have at all. The grid renders a placeholder for each.
   */
  url: string | null;
  /** The gallery photo `url` came from, so a click can open the album on it. */
  photoId: number | null;
}

/**
 * Always two slots, mom then dad, whatever the payload carries. A parent the
 * breeder hasn't recorded reads the same as a TBD one — either way that half of
 * the pairing isn't settled — so both fall back to the same "TBD" label.
 */
export function buildLitterMediaSlots(
  heroPhotos: LitterPageHeroPhotoResource[]
): LitterMediaSlot[] {
  return LITTER_MEDIA_ROLES.map(role => {
    const heroPhoto = heroPhotos.find(photo => photo.role === role);

    return {
      role,
      label: formatParentLabel(heroPhoto?.name, role),
      url: heroPhoto?.url ?? null,
      photoId: heroPhoto?.photo_id ?? null
    };
  });
}

/**
 * The label a gallery photo carries in the mobile carousel. A parent's photo
 * names that parent the same way the desktop hero does, a past litter's says
 * whose puppies they were, and the cover photo stands on its own.
 */
export function getLitterGalleryLabel(
  photo: LitterPageGalleryItemResource,
  heroPhotos: LitterPageHeroPhotoResource[]
): string | null {
  switch (photo.category) {
    case LITTER_GALLERY_CATEGORIES.PARENTS: {
      const parent = heroPhotos.find(
        heroPhoto => heroPhoto.dog_id === photo.dog_id
      );

      return parent ? formatParentLabel(parent.name, parent.role) : null;
    }
    case LITTER_GALLERY_CATEGORIES.PAST_LITTER:
      return PAST_LITTER_LABEL;
    default:
      return null;
  }
}

export function getLitterPagePath(litterId: number) {
  return `/litter/${litterId}`;
}

/**
 * Decides whether a litter opens the litter page or the litter modal. Every
 * place that opens a litter goes through here, so the rel_litter_page flag
 * only has to be removed in one spot.
 */
export function shouldOpenLitterPage(status: string | null | undefined) {
  return IS_LITTER_PAGE_ENABLED && LITTER_PAGE_STATUSES.includes(status);
}

interface OpenLitterPageOptions {
  /**
   * Defaults to a new tab on desktop and the same tab on mobile web, matching
   * how puppy pages open (see openPuppyPage).
   */
  newTab?: boolean;
}

export function openLitterPage(
  litterId: number,
  { newTab = !IS_MOBILE }: OpenLitterPageOptions = {}
) {
  const path = getLitterPagePath(litterId);

  if (newTab) {
    window.open(path, '_blank', 'noopener');
  } else {
    window.location.assign(path);
  }
}
